import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as api from "../api.js";
import { toast } from "react-toastify";


// ===============================
// GET ALL LEADS
// ===============================

export const GetAllLeadsSlice = createAsyncThunk(
  "customer/leads",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.GetAllLeadsApi();

      console.log("GetAllLeads response:", response);

      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (error) {
      console.error("API error:", error);

      return rejectWithValue(
        error.response?.data || {
          message: "An error occurred",
        }
      );
    }
  }
);


// ===============================
// GET LEAD DETAIL
// ===============================

export const GetLeadDetailSlice = createAsyncThunk(
  "admin/lead-detail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.GetLeadDetailApi(id);

      console.log("GetLeadDetail response:", response);

      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (error) {
      console.error("API error:", error);

      return rejectWithValue(
        error.response?.data || {
          message: "An error occurred",
        }
      );
    }
  }
);


// ===============================
// UPDATE LEAD STATUS
// ===============================

export const UpdateLeadStatusSlice = createAsyncThunk(
  "admin/lead-status",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.UpdateLeadStatusApi({
        id,
        data,
      });

      console.log(
        "UpdateLeadStatus response:",
        response
      );

      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (error) {
      console.error("API error:", error);

      return rejectWithValue(
        error.response?.data || {
          message: "An error occurred",
        }
      );
    }
  }
);


// ===============================
// ADD LEAD
// ===============================

export const AddLeadSlice = createAsyncThunk(
  "admin/add-lead",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.AddLeadApi(data);

      console.log("AddLead response:", response);

      if (
        response.status === 200 ||
        response.status === 201
      ) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (error) {
      console.error("AddLead API error:", error);

      return rejectWithValue(
        error.response?.data || {
          message: "Unable to add lead",
        }
      );
    }
  }
);


// ===============================
// ADD LEAD MESSAGE
// ===============================

export const AddLeadMessageSlice = createAsyncThunk(
  "admin/lead-message",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response =
        await api.AddLeadMessageApi({
          id,
          data,
        });

      console.log(
        "AddLeadMessage response:",
        response
      );

      if (response.status === 201) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (error) {
      console.error("API error:", error);

      return rejectWithValue(
        error.response?.data || {
          message: "An error occurred",
        }
      );
    }
  }
);


// ===============================
// DELETE LEAD
// ===============================

export const DeleteLeadSlice = createAsyncThunk(
  "admin/delete-lead",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.DeleteLeadApi(id);

      console.log("DeleteLead response:", response);

      if (response.status === 200) {
        return { ...response.data, id };
      } else {
        return rejectWithValue(response.data);
      }
    } catch (error) {
      console.error("API error:", error);

      return rejectWithValue(
        error.response?.data || {
          message: "Unable to delete lead",
        }
      );
    }
  }
);


// ===============================
// SLICE
// ===============================

const getErrorMessage = (action, fallback = "An error occurred") =>
  action.payload?.message || fallback;

const LeadManagementSlice = createSlice({
  name: "leadManagement",

  initialState: {
    leads: [],
    leadDetail: null,

    loading: false,
    detailLoading: false,
    addLeadLoading: false,
    statusUpdating: false,
    messageSubmitting: false,
    deletingId: null,

    error: null,
  },

  reducers: {
    ClearLeadDetail: (state) => {
      state.leadDetail = null;
    },
  },

  extraReducers: (builder) => {

    // ===============================
    // GET ALL LEADS
    // ===============================

    builder
      .addCase(GetAllLeadsSlice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(GetAllLeadsSlice.fulfilled, (state, action) => {
        state.leads = action.payload?.data || [];
        state.loading = false;
      })
      .addCase(GetAllLeadsSlice.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(action);
        toast.error(state.error);
      });


    // ===============================
    // GET LEAD DETAIL
    // ===============================

    builder
      .addCase(GetLeadDetailSlice.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(GetLeadDetailSlice.fulfilled, (state, action) => {
        state.leadDetail = action.payload?.data || null;
        state.detailLoading = false;
      })
      .addCase(GetLeadDetailSlice.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = getErrorMessage(action);
        toast.error(state.error);
      });


    // ===============================
    // UPDATE LEAD STATUS
    // ===============================

    builder
      .addCase(UpdateLeadStatusSlice.pending, (state) => {
        state.statusUpdating = true;
        state.error = null;
      })
      .addCase(UpdateLeadStatusSlice.fulfilled, (state, action) => {
        const { id, data } = action.meta.arg;

        state.statusUpdating = false;

        // Keep list and detail in sync without an extra round-trip
        const lead = state.leads.find((item) => item._id === id);
        if (lead) lead.status = data.status;
        if (state.leadDetail?._id === id) state.leadDetail.status = data.status;

        toast.success(action.payload?.message || "Lead status updated successfully");
      })
      .addCase(UpdateLeadStatusSlice.rejected, (state, action) => {
        state.statusUpdating = false;
        state.error = getErrorMessage(action);
        toast.error(state.error);
      });


    // ===============================
    // ADD LEAD
    // ===============================

    builder
      .addCase(AddLeadSlice.pending, (state) => {
        state.addLeadLoading = true;
        state.error = null;
      })
      .addCase(AddLeadSlice.fulfilled, (state, action) => {
        state.addLeadLoading = false;

        const newLead = action.payload?.data;
        if (newLead?._id) state.leads.unshift(newLead);

        toast.success(action.payload?.message || "Lead added successfully");
      })
      .addCase(AddLeadSlice.rejected, (state, action) => {
        state.addLeadLoading = false;
        state.error = getErrorMessage(action, "Unable to add lead");
        toast.error(state.error);
      });


    // ===============================
    // ADD LEAD MESSAGE
    // ===============================

    builder
      .addCase(AddLeadMessageSlice.pending, (state) => {
        state.messageSubmitting = true;
        state.error = null;
      })
      .addCase(AddLeadMessageSlice.fulfilled, (state, action) => {
        const updatedLead = action.payload?.data;

        state.messageSubmitting = false;

        if (updatedLead?._id) {
          state.leadDetail = updatedLead;

          const lead = state.leads.find((item) => item._id === updatedLead._id);
          if (lead) lead.nextFollowupDate = updatedLead.nextFollowupDate;
        }

        toast.success(action.payload?.message || "Message added successfully");
      })
      .addCase(AddLeadMessageSlice.rejected, (state, action) => {
        state.messageSubmitting = false;
        state.error = getErrorMessage(action);
        toast.error(state.error);
      });


    // ===============================
    // DELETE LEAD
    // ===============================

    builder
      .addCase(DeleteLeadSlice.pending, (state, action) => {
        state.deletingId = action.meta.arg;
        state.error = null;
      })
      .addCase(DeleteLeadSlice.fulfilled, (state, action) => {
        state.deletingId = null;
        state.leads = state.leads.filter((lead) => lead._id !== action.payload?.id);

        toast.success(action.payload?.message || "Lead deleted successfully");
      })
      .addCase(DeleteLeadSlice.rejected, (state, action) => {
        state.deletingId = null;
        state.error = getErrorMessage(action, "Unable to delete lead");
        toast.error(state.error);
      });
  },
});

export const { ClearLeadDetail } = LeadManagementSlice.actions;

export default LeadManagementSlice.reducer;
